import { useSyncExternalStore } from 'react';

/**
 * Which widgets somebody has rolled up.
 *
 * A board is a long page and most of it is not what you came for today. The
 * client asked to be able to minimise a widget, so a board can be left set up
 * the way they like it without deleting anything.
 *
 * Kept in the browser rather than on the board, for two reasons: rolling a
 * widget up is a glance-at-it preference, not a change to the board somebody
 * else also reads, and saving the whole board on every click would be a
 * request per fidget. It survives a reload, which is what "minimised" has to
 * mean to be worth having.
 *
 * It is a store rather than a `useState` per widget because **Collapse all**
 * has to reach widgets that are already on screen. Each one reading
 * `localStorage` once at mount cannot be told that something changed, so the
 * board would write the list and nothing would move until a reload.
 *
 * What the list holds is **the ids whose state differs from their screen's
 * default**, not "the collapsed ones". Widgets open by default, so for a board
 * the two readings are the same thing. The SOP register does not: a procedure
 * runs to 137 steps and opening all of them on arrival is nobody's idea of a
 * register. Passing `closedByDefault` flips how membership reads for those ids,
 * which keeps one store, one key and one Expand all / Collapse all menu instead
 * of a second set of each with the polarity reversed.
 */
const KEY = 'oneparacon.widgets.minimised';

const listeners = new Set<() => void>();

/** Read once, then held — `localStorage` is synchronous and this is read per widget per render. */
let ids: string[] | null = null;

function read(): string[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];

    // Anything else in there is somebody else's key or a half-written value.
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
  } catch {
    // Private windows and blocked site data throw rather than return null.
    return [];
  }
}

function snapshot(): string[] {
  if (ids === null) ids = read();

  return ids;
}

function write(next: string[]): void {
  ids = next;

  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Not being able to remember it is no reason not to do it.
  }

  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function useMinimised(id: string, closedByDefault = false): [boolean, () => void] {
  const listed = useSyncExternalStore(
    subscribe,
    () => snapshot().includes(id),
    () => false,
  );

  const toggle = () => {
    const all = snapshot().filter((entry) => entry !== id);

    write(listed ? all : [...all, id]);
  };

  return [closedByDefault ? !listed : listed, toggle];
}

/** How many of these are rolled up — what tells the board which way to offer. */
export function useMinimisedCount(candidates: string[], closedByDefault = false): number {
  return useSyncExternalStore(
    subscribe,
    () => {
      const current = snapshot();

      return candidates.reduce((count, id) => {
        const minimised = closedByDefault ? !current.includes(id) : current.includes(id);

        return minimised ? count + 1 : count;
      }, 0);
    },
    () => (closedByDefault ? candidates.length : 0),
  );
}

/** Roll up, or open out, a whole board at once. */
export function setMinimised(candidates: string[], minimised: boolean, closedByDefault = false): void {
  const rest = snapshot().filter((entry) => !candidates.includes(entry));

  // Listed means "not the default", so which way the write goes depends on
  // what the default is for these ids.
  write(minimised !== closedByDefault ? [...rest, ...candidates] : rest);
}
